import axiosInstance from 'config/axiosInstance';
import { useLatestRequest } from 'reusable/useLatestRequest';
import FetchFailed from 'reusable/FetchFailed';
import { businessMonth, businessRangeQuery, formatMonthDayTime, monthBounds } from 'config/time';
import { PAGE_SIZE } from 'config/constant';
import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import ExportRangeDialog, { ExportRange } from 'reusable/ExportRangeDialog';
import { fetchAllPages } from 'reusable/fetchAllPages';
import { useMonthPage } from 'reusable/useMonthPage';
import { CsvCondition, csvDateTime, deviceTimeZoneExportFields } from 'config/csv';
import DeviceTimeZone, { deviceTimeZoneOf } from 'reusable/DeviceTimeZone';
import Pagination from 'reusable/pagination';
import SortBy from 'reusable/sortby';
import TableScroll, { PAGE_TITLE_ID, tabId } from 'reusable/TableScroll';
import { useTranslation } from 'react-i18next';
import { pageToRecover } from 'reusable/pageRecovery';
import {
  HEALTH_QUESTION_CSV_HEADERS,
  HEALTH_QUESTION_KEYS,
  HEALTH_QUESTION_ORDER,
  healthAnswers,
  healthQuestionLabel,
} from 'config/healthQuestions';
import true_icon from '../../../../assets/icons/true.svg';
import false_icon from '../../../../assets/icons/false.svg';


interface DriverHealthCheckProps {
  id?: string;
  /** A36（2026-11-03 和田様の UAT）。ドライバー詳細の上部の月の切り替えで選んだ月（`YYYY-MM`）。既定は今の月。 */
  month?: string;
  /**
   * 2026-11-04 PM。ドライバー詳細の月の切り替え（DriverView が持つ）。車両の点検履歴と同じく、表の上の道具の行の左に置く。
   */
  monthNavigator?: React.ReactNode;
}

/** 記録の設問（details.driver_healthcheck[0].item）。 */
const itemsOf = (details: any) => details?.driver_healthcheck?.[0]?.item;

/** CSV の答え。true は OK（問題なし）、false は NG、無いときは空。 */
const okNg = (status: boolean | null | undefined) => (status === true ? 'OK' : status === false ? 'NG' : '');

/** 設問のキーから、保存される英語の文言（表示名の元）を引く。見出しの title に使う。 */
const QUESTION_TEXT: Readonly<Record<string, string>> = Object.entries(HEALTH_QUESTION_KEYS).reduce(
  (acc, [text, key]) => (acc[key] ? acc : { ...acc, [key]: text }),
  {} as Record<string, string>
);

const DriverHealthCheckTable: React.FC<DriverHealthCheckProps> = ({ id, month = businessMonth(), monthNavigator }) => {
  const { t } = useTranslation('driver');
  // 既定は新しい順（サーバーの並び順）。もう一方は記録の時刻の古い順。
  const sortingOptions = [
    { label: t('sort.default', { ns: 'common' }), value: '' },
    { label: t('sort.oldest', { ns: 'common' }), value: 'create_time' },
  ];
  const navigate = useNavigate();
  const [count, setCount] = useState(0);
  const [currentPage, setCurrentPage] = useMonthPage(month);
  const [sortValue, setSortValue] = useState('');
  const [driverHealthCheck, setDriverHealthCheck] = useState<any[]>([]);

  const handleSortChange = (sortValue: string) => {
    setSortValue(sortValue);
    setCurrentPage(1);
  };

  const exportFields = [
    // 健康点検の記録の項目。測定値・測定時刻（data / testing_time）はこの記録に無い。
    // ADM-11-AC1。行を画面・API の記録へたどれるよう、記録の ID を先頭に出す。
    { header: 'Record ID', field: 'id' },
    { header: 'Driver Name', field: 'user.name' },
    { header: 'Driver Email', field: 'user.email' },
    { header: 'Inspection Date', field: 'date' },
    { header: 'Maintenance Manager', field: 'maintenance_manager' },
    { header: 'Operations Manager', field: 'operations_manager' },
    // ADM-05。作成日時は日本時間で出す（UTC の ISO 文字列のままだった）。
    { header: 'Date & Time', field: 'create_time', customExport: csvDateTime },
    // A36（2026-11-03 和田様の UAT）。体温と設問ごとの答え（OK / NG）。以前の CSV には無かった。
    {
      header: 'Temperature',
      field: 'details',
      customExport: (details: any) => healthAnswers(itemsOf(details)).temperature,
    },
    ...HEALTH_QUESTION_ORDER.map((key) => ({
      header: HEALTH_QUESTION_CSV_HEADERS[key],
      field: 'details',
      customExport: (details: any) => okNg(healthAnswers(itemsOf(details)).answers[key]),
    })),
    // EV-116 段階3。送信の時点の端末の時間帯（無い記録は空欄）。
    ...deviceTimeZoneExportFields(),
  ];
  // ADM-11-AC2。出力した条件をファイル名に入れる。A36。範囲（period）はエクスポートの画面で足す。
  const exportConditions: CsvCondition[] = [
    ['driver', id],
    ['sort', sortValue],
  ];

  // 健康点検の期間は作成日時。check_in はこの記録に無く、無視されていた。
  // 並び替えは選んだ値を問い合わせに渡す。以前は選択肢だけで反映されていなかった。
  // range が null なら期間で絞らない（エクスポートの全件）。
  const listUrl = useCallback(
    (range: ExportRange, page: number, pageSize: number) => {
      let apiUrl = '/v1/health_inspection_records/?';
      if (range) apiUrl += businessRangeQuery('create_time', range.start, range.end);
      apiUrl += `order_by=${sortValue}&page=${page}&page_size=${pageSize}&user=${id}`;
      return apiUrl;
    },
    [id, sortValue]
  );
  // A36。エクスポートは選んだ範囲（この月／期間／全件）を全ページ読む。
  const fetchExportRows = useCallback(
    (range: ExportRange) => fetchAllPages((page, pageSize) => listUrl(range, page, pageSize)),
    [listUrl]
  );

  // ADM-15。取得の失敗（空の一覧と区別する）と、最後の要求の応答だけを反映するための番号。
  const [fetchError, setFetchError] = useState<unknown>(null);
  const beginRequest = useLatestRequest();

  const fetchData = useCallback(async () => {
    const isCurrent = beginRequest();
    try {
      // A36。表は選んだ月の分をページ送り。
      const response = await axiosInstance.get(listUrl(monthBounds(month), currentPage, PAGE_SIZE));
      if (!isCurrent()) return;
      // EV-460。今のページが無くなっていたら（空のページ・件数あり）、最後の有効なページを読む。
      const recover = pageToRecover(currentPage, PAGE_SIZE, {
        count: response.data.count,
        rows: response.data.results?.length ?? 0,
      });
      if (recover !== null) {
        setCurrentPage(recover);
        return;
      }
      if (!isCurrent()) return;
      setFetchError(null);
      setCount(response.data.count);
      setDriverHealthCheck(response.data.results);
    } catch (error) {
      if (!isCurrent()) return;
      // EV-460。ページの外（404）なら、エラーにせず1ページ目を読む。
      const recoverPage = pageToRecover(currentPage, PAGE_SIZE, { error });
      if (recoverPage !== null) {
        setCurrentPage(recoverPage);
        return;
      }
      console.error('Failed to fetch data', error);
      setDriverHealthCheck([]);
      setFetchError(error);
    }
  }, [listUrl, month, currentPage, beginRequest, setCurrentPage]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);


  const handleDetailsClick = (id: string) => {
    navigate(`/driver/healthcheck/${id}`);
  };

  const rows = driverHealthCheck.map((check) => ({ check, answers: healthAnswers(itemsOf(check.details)) }));
  // 対応表に無い設問（将来の設問など）は、表示中の記録に出てきた順に後ろの列へ足す（保存された name のまま）。
  const otherNames = Array.from(new Set(rows.flatMap(({ answers }) => answers.others.map((other) => other.name))));

  /** ✓ / ✗ のセル。答えが無いときは「-」。 */
  const mark = (status: boolean | null | undefined) =>
    status === true || status === false ? (
      <img
        className="h-6 inline-block"
        src={status ? true_icon : false_icon}
        alt={status ? t('ui.check-ok', { ns: 'common' }) : t('ui.check-ng', { ns: 'common' })}
      />
    ) : (
      '-'
    );

  const th = 'px-2 py-2 font-medium text-center whitespace-nowrap';
  const td = 'px-2 py-2 text-center whitespace-nowrap';

  return (
    <section className="flex flex-col space-y-2">
      <div className="flex flex-row space-x-2 items-center justify-between">
        {/* ToolBar Section。2026-11-04 PM。左に月の切り替え、右にソート・エクスポート（車両の点検履歴と同じ並び）。 */}
        {monthNavigator ?? <div />}
        <div className="flex flex-row space-x-2 items-center">
          <SortBy
            options={sortingOptions}
            defaultValue={sortValue}
            onChange={handleSortChange}
          />
          {/* A36。エクスポートは「この月／期間を指定／全件」から選ぶ（以前は表示中の1ページ分）。 */}
          <ExportRangeDialog
            month={month}
            exportFields={exportFields}
            name="driver_health_check"
            conditions={exportConditions}
            fetchRows={fetchExportRows}
          />
        </div>
      </div>
      {/* Table */}
      {/* A36（2026-11-03 和田様の UAT）。縦＝日付・横＝設問の表（車両の点検履歴と同じ向き）。1日に2回点検したら2行。
          設問の列が多いので、狭い幅では表の外枠の中だけを横に送る（EV-301）。 */}
      <div>
        <TableScroll labelledBy={`${PAGE_TITLE_ID} ${tabId('healthcheck')}`}>
          <table className="w-full border-collapse" data-testid="health-matrix">
            <thead>
              <tr className="bg-primary/50">
                <th scope="col" className={`${th} rounded-l-md`}>{t('view.health-check.submit-datetime')}</th>
                <th scope="col" className={th} title={healthQuestionLabel(QUESTION_TEXT.temperature, t)}>
                  {t('view.health-check.short.temperature')}
                </th>
                {HEALTH_QUESTION_ORDER.map((key) => (
                  <th key={key} scope="col" className={th} title={healthQuestionLabel(QUESTION_TEXT[key], t)}>
                    {t(`view.health-check.short.${key}`)}
                  </th>
                ))}
                {otherNames.map((name) => (
                  <th key={name} scope="col" className={th}>
                    {name}
                  </th>
                ))}
                <th scope="col" className={th}>{t('view.health-check.detail')}</th>
                <th scope="col" className={`${th} rounded-r-md`}>{t('time-zone.device', { ns: 'common' })}</th>
              </tr>
            </thead>
            <tbody>
              {rows.map(({ check, answers }) => {
                return (
                  <tr key={check.id} className="border-b">
                    {/* EV-116。業務の時間帯（会社の時間帯）で出す。2026-11-04 PM。日付と時刻を「日時」の1列に（月日 時分秒）。 */}
                    <td className={td}>{formatMonthDayTime(check.create_time)}</td>
                    {/* 体温は値を出す。基準以上（status が false）は赤で。 */}
                    <td className={`${td} ${answers.temperatureOk === false ? 'text-red-700 font-medium' : ''}`}>
                      {answers.temperature ? `${answers.temperature}℃` : '-'}
                    </td>
                    {HEALTH_QUESTION_ORDER.map((key) => (
                      <td key={key} className={td}>
                        {mark(answers.answers[key])}
                      </td>
                    ))}
                    {otherNames.map((name) => (
                      <td key={name} className={td}>
                        {mark(answers.others.find((other) => other.name === name)?.status)}
                      </td>
                    ))}
                    <td className={td}>
                      <button
                        type="button"
                        className="underline text-primary/300"
                        onClick={() => handleDetailsClick(check.id)}
                      >
                        {t('view.health-check.detail_button')}
                      </button>
                    </td>
                    {/* EV-116 段階3。送信の時点の端末の時間帯。 */}
                    <td className={td}>
                      <DeviceTimeZone {...deviceTimeZoneOf(check)} at={check.create_time} />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </TableScroll>
        {fetchError ? <FetchFailed error={fetchError} onRetry={fetchData} /> : null}
      </div>
      {/* Pagination Section */}
      <div>
        <Pagination
          allRows={count}
          rowsPerPage={PAGE_SIZE}
          currentPage={currentPage}
          onPageChange={setCurrentPage}
        />
      </div>
    </section>
  );
};

/**
 * 健康点検の一覧の「OK の数／設問の数」。設問の数は記録ごとに数える（2026-10-19。以前は 8 で固定で、
 * 体温の行（アプリが name・status・note で送る）が増えると健康な提出が 9/8 で赤、発熱でも 8/8 で緑になった）。
 * 以前のアプリの記録は体温を { 設問: "36.5" }（status なし）で保存していたので、分母は status が真偽の
 * 設問だけで数える（古い健康な記録を 8/9 にしない）。すべての設問が OK のときだけ allOk。
 */
export function healthCheckSummary(items: Array<{ status?: unknown }> | undefined) {
  const list = (Array.isArray(items) ? items : []).filter(
    (item) => typeof item?.status === 'boolean'
  );
  const ok = list.filter((item) => item.status === true).length;
  return { ok, total: list.length, allOk: list.length > 0 && ok === list.length };
}

export default DriverHealthCheckTable;
